import React, { useEffect, useState } from "react";
import { ContentCopy } from "@mui/icons-material";
import { Alert, Box, Button, IconButton, InputAdornment, Popover, TextField, Tooltip, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";

import { LoadingButton } from "ui";

import * as Auth from "fond/cognito/auth";
import { useAccountSwitching } from "fond/hooks/useAccountSwitching";
import { useSwitchableAccounts } from "fond/hooks/useSwitchableAccounts";
import AccountMenuItems from "fond/topBar/AccountMenuItems";
import { CognitoUser } from "fond/types";
import { buildTotpUri } from "fond/utils/auth";
import { PageCentred } from "fond/widgets/PageCentred";

interface MFASetupRequiredProps {
  onComplete: () => void;
}

const MFASetupRequired: React.FC<MFASetupRequiredProps> = ({ onComplete }) => {
  const { accounts } = useSwitchableAccounts();
  const { initiateSwitch, modal: accountSwitchingModal } = useAccountSwitching();
  const hasMultipleAccounts = accounts.length > 1;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [secret, setSecret] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const setup = async () => {
      setSetupLoading(true);
      try {
        const user: CognitoUser = await Auth.currentAuthenticatedUser();
        setEmail(user.getUsername());
        const secretKey = await Auth.setupTOTP(user);
        setSecret(secretKey);
      } catch {
        setError("Failed to initialise MFA setup. Please try again.");
      } finally {
        setSetupLoading(false);
      }
    };

    setup();
  }, []);

  const totpUri = buildTotpUri(email, secret);

  const handleCopySecret = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError("Please enter a 6-digit code.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const user: CognitoUser = await Auth.currentAuthenticatedUser();
      await Auth.verifyTotpToken(user, code);
      await Auth.setPreferredMFA(user, "TOTP");
      onComplete();
    } catch (err: unknown) {
      const errCode = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      if (errCode === "CodeMismatchException" || errCode === "EnableSoftwareTokenMFAException") {
        setError("Invalid verification code. Please check your authenticator app and try again.");
      } else {
        setError("An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (step === 1) return "Set Up Authenticator App";
    return "Verify Authenticator";
  };

  return (
    <PageCentred
      title={getTitle()}
      windowTitle="MFA Setup Required"
      description={<Typography>Your workspace requires multi-factor authentication. Set up an authenticator app to continue.</Typography>}
      rightAdornment={
        hasMultipleAccounts ? (
          <>
            <Button
              variant="outlined"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ textTransform: "uppercase" }}
              data-testid="switch-account-button"
            >
              Switch workspace
            </Button>
            <Popover
              open={Boolean(menuAnchor)}
              anchorEl={menuAnchor}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <AccountMenuItems
                mode="all"
                showManageLink={false}
                onAccountSwitch={() => setMenuAnchor(null)}
                onAccountClick={(id, name) => {
                  setMenuAnchor(null);
                  initiateSwitch(id, name);
                }}
              />
            </Popover>
          </>
        ) : undefined
      }
      bare
    >
      {step === 1 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 1 }}>
          <Typography variant="body2" sx={{ alignSelf: "flex-start" }}>
            Scan the QR code below with your authenticator app (e.g. Google Authenticator, Authy, or Microsoft Authenticator).
          </Typography>
          {setupLoading ? (
            <Box sx={{ height: 200, display: "flex", alignItems: "center" }}>
              <Typography color="text.secondary">Loading...</Typography>
            </Box>
          ) : (
            secret && (
              <>
                <Box sx={{ p: 2, bgcolor: "white", borderRadius: 1, border: 1, borderColor: "divider" }}>
                  <QRCodeSVG value={totpUri} size={200} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "flex-start" }}>
                  Or enter this key manually:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={secret}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: "monospace", fontSize: 13 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? "Copied!" : "Copy"}>
                          <IconButton size="small" onClick={handleCopySecret} aria-label="Copy MFA secret">
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )
          )}
          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", mt: 1 }}>
            <Button variant="contained" disabled={!secret || setupLoading} onClick={() => setStep(2)}>
              Next
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
          <Typography variant="body2">Enter the 6-digit verification code from your authenticator app to complete setup.</Typography>
          <TextField
            autoFocus
            fullWidth
            label="Verification code"
            value={code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "").slice(0, 6);
              setCode(val);
              setError(null);
            }}
            inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
            placeholder="000000"
          />
          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              onClick={() => {
                setStep(1);
                setCode("");
                setError(null);
              }}
            >
              Back
            </Button>
            <LoadingButton variant="contained" loading={loading} disabled={code.length !== 6} onClick={handleVerify}>
              Verify & Enable
            </LoadingButton>
          </Box>
        </Box>
      )}
      {accountSwitchingModal}
    </PageCentred>
  );
};

export default MFASetupRequired;
